import React, { useState } from 'react';
import axios from 'axios';
import { useAdmin } from '../../contexts/AdminContext';
import AdminLayout from '../../components/admin/AdminLayout';
import { Lock, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSettings = () => {
  const { getAuthHeaders, logout } = useAdmin();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'New passwords do not match!' });
      return;
    }

    if (newPassword.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters long!' });
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API}/admin/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword
        },
        getAuthHeaders()
      );
      
      setMessage({ type: 'success', text: 'Password changed successfully! Please login again.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      
      setTimeout(() => {
        logout();
        window.location.href = '/admin/login';
      }, 2000);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Error changing password. Please check your current password.' 
      });
    }
    setSaving(false);
  };

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-2xl">
        <div>
          <h1 className="display-medium text-white mb-2">Account Settings</h1>
          <p className="body-medium text-white/85">Manage your admin account settings</p>
        </div>

        <div className="bg-white/10 border border-white/25 p-6 rounded-lg">
          <h2 className="heading-2 text-white mb-6 flex items-center gap-2">
            <Lock size={24} /> Change Password
          </h2>
          
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="body-small text-white mb-1 block">Current Password</label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded focus:outline-none focus:border-[#00FFD1]"
                required
              />
            </div>

            <div>
              <label className="body-small text-white mb-1 block">New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded focus:outline-none focus:border-[#00FFD1]"
                required
                minLength="8"
              />
              <p className="body-small text-white/60 mt-1">Minimum 8 characters</p>
            </div>

            <div>
              <label className="body-small text-white mb-1 block">Confirm New Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full bg-white/10 border border-white/25 text-white px-4 py-2 rounded focus:outline-none focus:border-[#00FFD1]"
                required
                minLength="8"
              />
            </div>

            {message.text && (
              <div className={`p-3 rounded-lg ${
                message.type === 'success' 
                  ? 'bg-green-500/10 border border-green-500/50 text-green-500' 
                  : 'bg-red-500/10 border border-red-500/50 text-red-500'
              }`}>
                <p className="body-small">{message.text}</p>
              </div>
            )}

            <button 
              type="submit" 
              disabled={saving}
              className="btn-primary flex items-center gap-2"
            >
              <Save size={20} /> {saving ? 'Changing Password...' : 'Change Password'}
            </button>
          </form>
        </div>

        <div className="bg-[#00FFD1]/10 border border-[#00FFD1]/30 p-6 rounded-lg">
          <h3 className="heading-3 text-[#00FFD1] mb-2">Security Tips</h3>
          <ul className="body-small text-white/85 space-y-2">
            <li>• Use a strong password with at least 8 characters</li>
            <li>• Include uppercase, lowercase, numbers, and special characters</li>
            <li>• Do not share your admin credentials with anyone</li>
            <li>• Change your password regularly</li>
            <li>• You will be logged out after changing your password</li>
          </ul>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;
